import { ITimeBlockComponentItem } from '../../types/time-block-component-item-types';
import { DateTimeHelper } from '../../../../util/date-time-helper';
import { TimeBlockElementSelectorService } from '../../rendering/time-block-element-selector.service';
import { CalendarService } from '../../../../services/calendar.service';
import { TimeCoordinateMappingService } from '../../../../time-mapping/time-coordinate-mapping.service';
import { TimeBlockType } from '../../types/time-block-types';
import { DayOrWeekCalendarModel } from '../../../../../../core/models/calendar/day-or-week-calendar.model';
import { TimeBlockGeometryData } from '../../../../../../core/models/timeblock/time-block-geometry-data.model';
import { CalendarGeometryService } from '../../../../services/calendar-geometry.service';
import { MonthCalendarModel } from '../../../../../../core/models/calendar/month-calendar.model';
import { CalendarViews } from '../../../../calendar-types';
import { TimeBlockDayOrWeekFixIntervalType } from '../../../../../../core/models/timeblock/time-block-view-type.model';
import { TimeBlockService } from '../../services/time-block.service';
import { CalendarServiceHelper } from '../../../../services/calendar-service-helper';
import { assert } from 'src/app/core/assert/assert';

export class TimeBlockGeometryCalculator {
  constructor(
    private readonly calendarService: CalendarService,
    private readonly calendarGeometryService: CalendarGeometryService,
    private readonly timeCoordinateMapperService: TimeCoordinateMappingService,
    private readonly timeBlockService: TimeBlockService,
  ) {}

  public initializeVariableIntervalTimeBlockGeometry(
    timeBlock: ITimeBlockComponentItem,
  ): TimeBlockGeometryData {
    const calendarView = this.calendarService.model.calendarViewMode;
    if (calendarView === CalendarViews.MonthGrid) {
      return this.calculateMonthViewVariableIntervalTimeBlockGeometry(timeBlock);
    }
    return this.calculateDayWeekViewVariableIntervalTimeBlockGeometry(timeBlock);
  }

  public initializeFixIntervalTimeBlockGeometry(
    timeBlock: ITimeBlockComponentItem,
  ): TimeBlockGeometryData {
    const view = this.calendarService.model.calendarViewMode;
    const geometry = new TimeBlockGeometryData();
    Object.assign(geometry, timeBlock.timeBlockModel.timeBlockViewType.geometryData);

    const calculator =
      view === CalendarViews.WeekGrid
        ? new WeekViewFixIntervalCalculator(
            this.calendarService,
            this.calendarGeometryService,
            this.timeBlockService,
          )
        : new MonthViewFixIntervalCalculator(this.calendarService, this.calendarGeometryService);
    return calculator.calculateHorizontalGeometry(timeBlock);
  }

  private calculateDayWeekViewVariableIntervalTimeBlockGeometry(
    timeBlock: ITimeBlockComponentItem,
  ): TimeBlockGeometryData {
    if (!timeBlock) {
      return;
    }

    const geometry = new TimeBlockGeometryData();
    Object.assign(geometry, timeBlock.timeBlockModel.timeBlockViewType.geometryData);
    // Calculate each block length and calculate attribute values
    this.calcVerticalGeometry(timeBlock, geometry);
    // Calculate left offset and width for existing and dragging time blocks
    // (so no blocks which will be removed after dropping the drag clone).
    if (timeBlock.timeBlockModel.type !== TimeBlockType.GhostBlock) {
      this.updateVariableIntervalTimeBlockOffsetsAndWidths(timeBlock, geometry);
    }
    return geometry;
  }

  private calculateMonthViewVariableIntervalTimeBlockGeometry(
    timeBlock: ITimeBlockComponentItem,
  ): TimeBlockGeometryData {
    const timeBlockHTMLWRapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);
    const timeBlockBCR = timeBlockHTMLWRapper.getBoundingClientRect();
    const calendarGeometry = this.calendarService.model.geometryData;

    const geometryData = new TimeBlockGeometryData();
    Object.assign(calendarGeometry, timeBlock.timeBlockModel.timeBlockViewType.geometryData);
    geometryData.left =
      Math.abs(timeBlockBCR.left) - this.calendarService.model.geometryData.calendarBodyOffsetLeft;
    const calendarRightEdge =
      calendarGeometry.calendarBodyOffsetLeft + calendarGeometry.calendarBodyWidth;
    geometryData.right = calendarRightEdge - Math.abs(timeBlockBCR.left);
    geometryData.height = timeBlockBCR.height;
    return geometryData;
  }

  private updateVariableIntervalTimeBlockOffsetsAndWidths(
    timeBlock: ITimeBlockComponentItem,
    geometryData: TimeBlockGeometryData,
  ): void {
    const timeBlockHTMLWRapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);
    const timeBlockBCR = timeBlockHTMLWRapper.getBoundingClientRect();
    const calendarGeometry = this.calendarService.model.geometryData;

    geometryData.left =
      Math.abs(timeBlockBCR.left) - this.calendarService.model.geometryData.calendarBodyOffsetLeft;
    const calendarRightEdge =
      calendarGeometry.calendarBodyOffsetLeft + calendarGeometry.calendarBodyWidth;
    geometryData.right = calendarRightEdge - Math.abs(timeBlockBCR.left);

    const calendarModel = this.calendarService.model as DayOrWeekCalendarModel;
    const visibleCalendarStart = calendarModel.calendarProperties.visibleStartDate;
    const visibleCalendarEnd = calendarModel.calendarProperties.visibleEndDate;

    let startDate;
    if (
      DateTimeHelper.isBefore(
        timeBlock.timeBlockModel.start,
        calendarModel.fixIntervalCalendarModel.calendarProperties.offsetStartDate,
      )
    ) {
      startDate = calendarModel.fixIntervalCalendarModel.calendarProperties.offsetStartDate;
    } else {
      startDate = timeBlock.timeBlockModel.start;
    }

    const timeBlockPartDate = DateTimeHelper.addDays(
      startDate,
      timeBlock.timeBlockModel.partNumber,
    );

    const prevDaysCount = DateTimeHelper.differenceInCalendarDays(
      visibleCalendarStart,
      timeBlockPartDate,
    );
    const nextDaysCount = DateTimeHelper.differenceInCalendarDays(
      timeBlockPartDate,
      visibleCalendarEnd,
    );

    if (prevDaysCount > 0) {
      // Set left offset if time block part is outside of the visible calendar view.
      geometryData.left =
        this.calendarGeometryService.getDayOrWeekVariableIntervalLaneWidth() * prevDaysCount * -1;
    } else if (nextDaysCount > 0) {
      // Set right offset if time block part is outside of the visible calendar view.
      geometryData.right =
        this.calendarGeometryService.getDayOrWeekVariableIntervalLaneWidth() * nextDaysCount;
    }
    // Set initially a width of 100%;
    geometryData.width = 100;
  }

  private calcVerticalGeometry(
    timeBlock: ITimeBlockComponentItem,
    geometryData: TimeBlockGeometryData,
  ): void {
    const timeBlockModel = timeBlock.timeBlockModel;

    let start = timeBlockModel.start;
    let end = timeBlockModel.end;

    const currentPartDay = DateTimeHelper.addDays(start, timeBlockModel.partNumber);
    const startOfDay = DateTimeHelper.startOfDay(currentPartDay);

    if (!DateTimeHelper.isSameDay(start, end)) {
      if (DateTimeHelper.isMidnight(end)) {
        end = DateTimeHelper.transformToNearlyMidnightIfNeeded(end);
      }

      if (timeBlockModel.partNumber === 0) {
        end = DateTimeHelper.endOfDay(start);
      } else if (timeBlockModel.partNumber === timeBlockModel.partCount - 1) {
        start = DateTimeHelper.startOfDay(end);
      } else {
        start = startOfDay;
        end = DateTimeHelper.endOfDay(start);
      }
    }

    end = DateTimeHelper.transformToExactlyMidnightIfNeeded(end, true);
    const height = this.timeCoordinateMapperService.mapDurationToScreenLength(start, end);
    geometryData.height = height || 1;
    geometryData.top = this.timeCoordinateMapperService.mapDurationToScreenLength(
      startOfDay,
      start,
    );

    if (geometryData.height <= 0) {
      throw new Error(`Invalid geometry data height: ${geometryData.height}`);
    }

    // Because of rounding errors it can happen that bottom becomes less than 0.
    geometryData.bottom = Math.max(
      0,
      this.calendarService.model.geometryData.calendarBodyHeight - geometryData.top - height,
    );
    geometryData.calendarTopEdgeToTimeBlockBottomEdge = geometryData.top + geometryData.height;
    geometryData.calendarBottomEdgeToTimeBlockTopEdge = geometryData.bottom + geometryData.height;

    if (
      geometryData.top < 0 ||
      geometryData.calendarTopEdgeToTimeBlockBottomEdge < 0 ||
      geometryData.calendarBottomEdgeToTimeBlockTopEdge < 0
    ) {
      throw new Error('Invalid geometry data.');
    }
  }
}

interface Calculator {
  calculateHorizontalGeometry: (timeBlock: ITimeBlockComponentItem) => TimeBlockGeometryData;
}

class WeekViewFixIntervalCalculator implements Calculator {
  constructor(
    private readonly calendarService: CalendarService,
    private readonly calendarGeometryService: CalendarGeometryService,
    private readonly timeBlockService: TimeBlockService,
  ) {}

  public calculateHorizontalGeometry(timeBlock: ITimeBlockComponentItem): TimeBlockGeometryData {
    const geometry = new TimeBlockGeometryData();

    const timeBlockHTMLWRapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);
    const timeBlockBCR = timeBlockHTMLWRapper.getBoundingClientRect();
    const calendarModel = this.calendarService.model as DayOrWeekCalendarModel;
    const viewType = timeBlock.timeBlockModel
      .timeBlockViewType as TimeBlockDayOrWeekFixIntervalType;
    let visibleLaneIndex = CalendarServiceHelper.toVisibleLaneIndex(
      calendarModel.fixIntervalCalendarModel,
      viewType.laneIndexStart,
    );

    // For the transforming block, we render the full time block (i.e. also the part outside the visible calendar view).
    // Otherwise, we set left to 0 to render only the visible portion of the time block.
    if (
      timeBlock.timeBlockModel.type === TimeBlockType.ExistingBlock &&
      visibleLaneIndex < 0 &&
      !this.timeBlockService.isTimeBlockOutsideOfView(timeBlock)
    ) {
      visibleLaneIndex = 0;
    }

    geometry.width = this.calculateFixIntervalWidth(timeBlock); // Derived from $gap in time-block.scss
    geometry.left =
      this.calendarGeometryService.getDayOrWeekFixIntervalLaneWidth() * visibleLaneIndex;
    geometry.calendarTopEdgeToTimeBlockBottomEdge = timeBlockBCR.height;
    geometry.height = timeBlockBCR.height;
    geometry.top = 0;
    return geometry;
  }

  private calculateFixIntervalWidth(timeBlock: ITimeBlockComponentItem): number {
    const cellWidth = this.calendarGeometryService.getDayOrWeekFixIntervalLaneWidth();
    const calendarModel = this.calendarService.model as DayOrWeekCalendarModel;

    let start = timeBlock.timeBlockModel.start;
    if (
      DateTimeHelper.isBefore(
        timeBlock.timeBlockModel.start,
        calendarModel.fixIntervalCalendarModel.calendarProperties.offsetStartDate,
      )
    ) {
      start = calendarModel.fixIntervalCalendarModel.calendarProperties.offsetStartDate;
    }

    let end = timeBlock.timeBlockModel.end;
    if (
      DateTimeHelper.isAfter(
        timeBlock.timeBlockModel.end,
        calendarModel.fixIntervalCalendarModel.calendarProperties.offsetEndDate,
      )
    ) {
      end = calendarModel.fixIntervalCalendarModel.calendarProperties.offsetEndDate;
    }

    let cellCount: number;
    // For the transforming a non-existing block, we render the full time block (i.e. also the part outside the visible calendar view).
    if (
      timeBlock.timeBlockModel.type === TimeBlockType.TransformingBlock ||
      timeBlock.timeBlockModel.type === TimeBlockType.NonExistingBlock
    ) {
      cellCount = DateTimeHelper.differenceInDays(end, start) + 1;
    } else {
      // For an existing block, we render just the time block portion within the visible calendar view.
      cellCount = this.timeBlockService.getTimeBlockVisiblePartCount(timeBlock) + 1;
    }

    // Exclude border
    const firstLane =
      calendarModel.fixIntervalCalendarModel.timeBlockHtmlContainers[0].nativeElement;
    const borderWidth = this.calendarGeometryService.getLaneRightBorderWidth(firstLane);
    return cellWidth * cellCount - borderWidth;
  }
}

class MonthViewFixIntervalCalculator implements Calculator {
  constructor(
    private readonly calendarService: CalendarService,
    private readonly calendarGeometryService: CalendarGeometryService,
  ) {}

  public calculateHorizontalGeometry(timeBlock: ITimeBlockComponentItem): TimeBlockGeometryData {
    const geometry = new TimeBlockGeometryData();
    const timeBlockHTMLWRapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlock);
    const timeBlockBCR = timeBlockHTMLWRapper.getBoundingClientRect();

    geometry.width = this.calculateWidth(timeBlock);
    geometry.height = timeBlockBCR.height;

    assert(geometry.height > 0);

    return geometry;
  }

  /**
   *
   * Calculate the width in pixels
   */
  private calculateWidth(timeBlock: ITimeBlockComponentItem): number {
    const cellWidth = this.calendarGeometryService.getMonthFixLaneWidth();
    const calendarModel = this.calendarService.model as MonthCalendarModel;
    const timeBlockHeadDate = DateTimeHelper.isBefore(
      timeBlock.timeBlockModel.start,
      calendarModel.calendarProperties.visibleStartDate,
    )
      ? calendarModel.calendarProperties.visibleStartDate
      : timeBlock.timeBlockModel.start;
    const currentDate = DateTimeHelper.addDays(
      timeBlockHeadDate,
      timeBlock.timeBlockModel.partNumber,
    );

    const currentStartOfWeek = DateTimeHelper.startOfWeek(currentDate, {
      weekStartsOn: calendarModel.calendarProperties.weekStartsOn,
    });
    const start = DateTimeHelper.isBefore(timeBlock.timeBlockModel.start, currentStartOfWeek)
      ? currentStartOfWeek
      : timeBlock.timeBlockModel.start;

    const currentEndOfWeek = DateTimeHelper.endOfWeek(currentDate, {
      weekStartsOn: calendarModel.calendarProperties.weekStartsOn,
    });
    const end = DateTimeHelper.isAfter(timeBlock.timeBlockModel.end, currentEndOfWeek)
      ? currentEndOfWeek
      : timeBlock.timeBlockModel.end;

    const cellCount = DateTimeHelper.differenceInDays(end, start) + 1;

    // Exclude border
    const firstLane =
      calendarModel.fixIntervalCalendarModel.timeBlockHtmlContainers[0].nativeElement;
    const borderWidth = this.calendarGeometryService.getLaneRightBorderWidth(firstLane);
    return cellWidth * cellCount - borderWidth * cellCount - borderWidth; // Subtract all cells' right borders and one left border
  }
}
